import React, { useState } from 'react';

import type { UUID } from '@aline/common';
import { link } from '@aline/routes';
import { IconButton, Typography } from '@mui/material';
import { Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AlineInput } from 'src/AlineInput';
import type { ConfirmationDialogProps } from 'src/components';
import { AlineTooltip, ConfirmationDialog, SelectButton } from 'src/components';
import { CancelXIcon } from 'src/components/icons/CancelXIcon';
import { IconColors } from 'src/components/icons/icon-utils';
import { Apollo } from 'src/gql/generated';
import { useCheckWorkflowAuthorization, useDeleteWorkflow, useGetWorkflowSimple } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  deleteWrapper: {
    padding: theme.spacing(2),
  },
  discardText: {
    color: IconColors.Red,
  },
  icon: {
    marginRight: theme.spacing(2),
  },
}));

const confirmString = 'DELETE';

function useWorkflowNoun(workflowId: UUID) {
  const { workflow } = useGetWorkflowSimple(workflowId);
  return workflow?.variant === Apollo.WorkflowVariant.Template ? 'template' : 'workflow';
}

export function DeleteWorkflowButton({ workflowId }: { workflowId: UUID }): JSX.Element | null {
  const { classes } = useStyles();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const noun = useWorkflowNoun(workflowId);

  return (
    <>
      <SelectButton onClick={() => setDeleteDialogOpen(!deleteDialogOpen)}>
        <CancelXIcon className={classes.icon} />
        <Typography className={classes.discardText}>Delete {noun}</Typography>
      </SelectButton>
      <ConfirmDeleteWorkflowDialog
        open={deleteDialogOpen}
        workflowId={workflowId}
        onClose={() => setDeleteDialogOpen(false)}
      />
    </>
  );
}

export function DeleteWorkflowIconButton({ workflowId }: { workflowId: UUID }): JSX.Element | null {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const noun = useWorkflowNoun(workflowId);

  return (
    <>
      <AlineTooltip arrow placement="top" title={`Delete ${noun}`}>
        <IconButton size="small" onClick={() => setDeleteDialogOpen(!deleteDialogOpen)}>
          <Trash2 size={18} />
        </IconButton>
      </AlineTooltip>
      <ConfirmDeleteWorkflowDialog
        open={deleteDialogOpen}
        workflowId={workflowId}
        onClose={() => setDeleteDialogOpen(false)}
      />
    </>
  );
}

interface ConfirmDeleteWorkflowDialogProps extends Pick<
  ConfirmationDialogProps,
  'open' | 'onClose'
> {
  workflowId: UUID;
}

export function ConfirmDeleteWorkflowDialog({
  onClose,
  open,
  workflowId,
}: ConfirmDeleteWorkflowDialogProps): JSX.Element {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { deleteWorkflow } = useDeleteWorkflow(workflowId);
  const [deleteText, setDeleteText] = useState<string>('');
  const { canEditWorkflow } = useCheckWorkflowAuthorization(workflowId);
  const noun = useWorkflowNoun(workflowId);

  async function handleDeleteWorkflow(): Promise<void> {
    const { data } = await deleteWorkflow();
    if (data) navigate(link.WorkflowList());
  }

  return (
    <ConfirmationDialog
      message={`Are you sure you want to delete the ${noun}? All documents will be PERMANENTLY DELETED. This action cannot be undone.`}
      open={open}
      primaryDisabledReason={
        !canEditWorkflow
          ? 'Not authorized to delete'
          : deleteText !== confirmString
            ? `Type ${confirmString} to proceed`
            : ''
      }
      primaryText="Delete"
      title={`Delete ${noun}`}
      onClickPrimary={handleDeleteWorkflow}
      onClose={onClose}
      onExit={() => setDeleteText('')}
    >
      <div className={classes.deleteWrapper}>
        <AlineInput
          fullWidth
          placeholder={`Type ${confirmString} to confirm...`}
          value={deleteText}
          onChange={e => setDeleteText(e.target.value)}
        />
      </div>
    </ConfirmationDialog>
  );
}
